import SolicitacaoChatApi from '@/Apis/Agendamento/Services/SolicitacaoChatApi'
import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators'
import { ISolicitacaoChatMessage, ISolicitacaoChatMessageType, SolicitacaoChatMessageTypeEnum } from '@/types/interfaces/solicitacao-chat/solicitacao-chat-message.interface'
import EventBus from '@/plugins/EventBus'
import { PendenciaSolicitacao } from '@/types/core'
import { Pendencia } from '@/types/_'
import { Solicitacao } from '../../../types/interfaces/Solicitacao'
import { ISolicitacaoChat } from '../../../types/interfaces/solicitacao-chat/solicitacao-chat.interface'

@Module({ namespaced: true, name: 'ChatAgendamentoPendenciaStore' })
export default class ChatAgendamentoPendenciaStore extends VuexModule {
  private open = false
  loading = false

  solicitationId = ''
  solicitation = {} as Solicitacao
  senderId = ''
  responsibleUserId?: string = ''
  responsibleUserName?: string = ''
  pendencyId = ''

  canEdit = true
  finished: boolean | null = false

  recipientName = ''
  recipientLastSeenDate = ''
  recipientOnline = false
  attachmentLoading = false

  pendenciaSolicitacao: PendenciaSolicitacao.IPendenciaSolicitacao = new PendenciaSolicitacao.PendenciaSolicitacao()

  messages: ISolicitacaoChatMessage[] = []

  @Action
  async sendMessage(data: ISolicitacaoChatMessage) {
    // Send a message of type open
    let messageType: SolicitacaoChatMessageTypeEnum
    if (data.anexoId) {
      messageType = SolicitacaoChatMessageTypeEnum.anexo
    } else {
      messageType = SolicitacaoChatMessageTypeEnum.texto
    }

    const messageIndex = this.messages.length

    // Add the message before create in api
    const localMessage: Partial<ISolicitacaoChatMessage> = {
      texto: data.texto,
      tipoMensagem: { display: '', key: messageType },
      usuarioId: this.senderId,
      anexoUrl: data.anexoUrl,
      anexoNome: data.anexoNome,
      anexoTipo: 'any',
    }

    this.context.commit('addMessage', localMessage)

    const sendAt = new Date().toISOString()
    const tipoMensagem = messageType === SolicitacaoChatMessageTypeEnum.anexo ? 'Documento' : 'Texto'
    const pendencyMessage: IPendencyMessage = {
      mensagemHospital: false,
      id: '',
      dataEnvio: sendAt,
      tipoMensagem,
      texto: data.texto,
      documentoId: data.anexoId!,
      nomeArquivo: data.anexoNome,
      url: data.anexoUrl,
    }

    const pendencia: Pendencia = {
      id: this.pendencyId,
      mensagens: [pendencyMessage],
      solicitacaoId: this.solicitationId,
    }
    const { solicitacaoId } = pendencia

    const res = await this.pendenciaSolicitacao.sendEnviarMensagem(solicitacaoId, pendencia)
    if (!res.success) {
      EventBus.$emit('setSnackbar', { show: true, text: res.message || 'Erro ao enviar mensagem', icon: 'warning', color: '#C62828' })
      // eslint-disable-next-line no-throw-literal
      throw 'Erro ao enviar mensagem'
    }

    // After message creation update the created at date
    localMessage.dataDeEnvio = sendAt

    // After message creation update the created message
    this.context.commit('setMessage', { index: messageIndex, message: localMessage })

    return res.data
  }

  @Action
  async openChatModal(data: IOpenChatModalData) {
    const {
      solicitationId,
      recipientName,
      senderId,
    } = data

    this.context.commit('setOpen', true)
    this.context.commit('setSenderId', senderId)
    this.context.commit('setSolicitationId', solicitationId)
    this.context.commit('setRecipientName', recipientName)

    EventBus.$emit('setLoadingRequest', { status: '', text: 'Carregando...' })

    await this.context.dispatch('loadChatData')

    EventBus.$emit('clearLoadingRequest')
  }

  @Action
  async loadChatData() {
    const [res, messagesRes] = await Promise.all([
      SolicitacaoChatApi.getChatBySolicitationId(this.solicitationId),
      this.pendenciaSolicitacao.getMensagemAberta(this.solicitationId),
    ])

    if (res.success && messagesRes.success) {
      const pendencyData = messagesRes.data as IPendencyChat

      this.context.commit('setPendencyId', pendencyData.pendenciaId)

      // Convert pendecy messages to chatx messages
      let dataDaUltimaVisualizacaoDoBackoffice = ''
      const mensagens = pendencyData.mensagens.map<ISolicitacaoChatMessage>((elem: IPendencyMessage) => {
        // Set user
        let usuarioId: string
        let usuarioNome: string = ''
        if (elem.mensagemHospital) {
          usuarioId = 'receiver'
          usuarioNome = this.recipientName

          dataDaUltimaVisualizacaoDoBackoffice = elem.dataEnvio
        } else {
          usuarioId = this.senderId
        }

        // Set message type. If is attachment, set attachments data
        let tipo: SolicitacaoChatMessageTypeEnum
        let tipoMensagem: ISolicitacaoChatMessageType
        let anexoNome = ''
        let anexoUrl = ''
        let anexoTipo = ''
        if (elem.tipoMensagem === 'Documento') {
          tipo = SolicitacaoChatMessageTypeEnum.anexo
          tipoMensagem = {
            display: 'Anexo',
            key: 'Anexo',
          }

          anexoNome = elem.nomeArquivo!
          anexoUrl = elem.url!
          anexoTipo = 'any'
        } else {
          tipo = SolicitacaoChatMessageTypeEnum.texto
          tipoMensagem = {
            display: 'Texto',
            key: 'Texto',
          }
        }

        const data: ISolicitacaoChatMessage = {
          tipo,
          texto: elem.texto,
          dataDeEnvio: elem.dataEnvio,
          usuarioId,
          usuarioNome,
          tipoMensagem,
          anexoNome,
          anexoUrl,
          anexoTipo,
        }

        return data
      })

      const data = res.data as ISolicitacaoChat
      this.context.dispatch('setChatData', { ...data, mensagens, dataDaUltimaVisualizacaoDoBackoffice })
    }
  }

  @Action
  async setChatData(data: ISolicitacaoChat) {
    if (data.usuarioBackofficeResponsavelNome) {
      this.context.commit('setRecipientName', data.usuarioBackofficeResponsavelNome)
    }

    this.context.commit('setRecipientLastSeenDate', data.dataDaUltimaVisualizacaoDoBackoffice)
    this.context.commit('setMessages', data.mensagens)
    this.context.commit('setSolicitation', data.solicitacao)
    this.context.commit('setFinished', data.chatFinalizado !== undefined ? data.chatFinalizado : null)
  }

  @Action
  async sendAttachment(file: File) {
    this.context.commit('setAttachmentLoading', true)
    const formData = new FormData()
    formData.append('laudos', file)

    const data: IPendencyUpload = {
      formData,
      hospitalId: this.solicitation.hospitalId,
      tipoDocumento: 'PendenciaHospital',
    }

    const res = await this.pendenciaSolicitacao.uploadDocumento(data)
    if (!res.success) {
      EventBus.$emit('setSnackbar', { show: true, text: 'Erro ao enviar mensagem', icon: 'warning', color: '#C62828' })
      this.context.commit('setAttachmentLoading', false)
      // eslint-disable-next-line no-throw-literal
      throw 'Erro ao enviar mensagem'
    }

    const resData = res.data as { id: string, nomeArquivo: string, url: string }[]
    const uploadedData = resData[0]

    this.sendMessage({
      tipo: SolicitacaoChatMessageTypeEnum.anexo,
      texto: '',
      anexoId: uploadedData.id,
      anexoNome: uploadedData.nomeArquivo,
      anexoUrl: uploadedData.url,
    })
    this.context.commit('setAttachmentLoading', false)
  }

  @Action
  async reply() {
    const res = await this.pendenciaSolicitacao.medicoResponde(this.solicitationId)
    if (res.success) {
      EventBus.$emit('setSnackbar', { show: true, text: 'Pendência respondida com sucesso!', icon: 'done', color: '#2E7D32' })
    } else {
      EventBus.$emit('setSnackbar', { show: true, text: res.message, icon: 'warning', color: '#C62828' })
      // eslint-disable-next-line no-throw-literal
      throw 'Erro ao responder pendência'
    }
  }

  @Action
  async closeChatModal() {
    this.context.commit('setOpen', false)
    this.context.dispatch('reset')
  }

  @Mutation
  setOpen(value: boolean) {
    this.open = value
  }

  @Mutation
  setSolicitationId(value: string) {
    this.solicitationId = value
  }

  @Mutation
  setSenderId(value: string) {
    this.senderId = value
  }

  @Mutation
  setRecipientName(value: string) {
    this.recipientName = value
  }

  @Mutation
  setRecipientLastSeenDate(value: string) {
    this.recipientLastSeenDate = value
  }

  @Mutation
  setResponsibleUserId(value: string) {
    this.responsibleUserId = value
  }

  @Mutation
  setResponsibleUserName(value: string) {
    this.responsibleUserName = value
  }

  @Mutation
  setAttachmentLoading(value: boolean) {
    this.attachmentLoading = value
  }

  @Mutation
  addMessage(value: ISolicitacaoChatMessage) {
    this.messages.push(value)
  }

  @Mutation
  setMessage(data: { index: number, message: ISolicitacaoChatMessage }) {
    this.messages.splice(data.index, 1, data.message)
  }

  @Mutation
  setMessages(value: ISolicitacaoChatMessage[]) {
    this.messages = value
  }

  @Mutation
  setCanEdit(value: boolean) {
    this.canEdit = value
  }

  @Mutation
  setSolicitation(value: Solicitacao) {
    this.solicitation = value
  }

  @Mutation
  setFinished(value: boolean) {
    this.finished = value
  }

  @Mutation
  setLoading(value: boolean) {
    this.loading = value
  }

  @Mutation
  setPendencyId(value: string) {
    this.pendencyId = value
  }

  @Action
  reset() {
    this.context.commit('setSolicitationId', '')
    this.context.commit('setPendencyId', '')
    this.context.commit('setRecipientName', '')
    this.context.commit('setRecipientLastSeenDate', '')
    this.context.commit('setMessages', [])
    this.context.commit('setCanEdit', [])
    this.context.commit('setSolicitation', {})
  }

  // Getters

  get isOpen() {
    return this.open
  }

  get isNew() {
    return [null, undefined, true].includes(this.finished)
  }

  get canMarkAsRead() {
    return this.finished
  }

  get canOpenConnection() {
    return this.finished || this.finished === false
  }
}

interface IOpenChatModalData {
  solicitationId: string;
  recipientName: string;
  senderId: string;
}

interface IPendencyChat {
  pendenciaId: string;
  mensagens: IPendencyMessage[];
}

interface IPendencyMessage {
  dataEnvio: string;
  id: string;
  mensagemHospital: boolean;
  texto: string;
  tipoMensagem: string;
  documentoId: string;
  nomeArquivo?: string;
  url?: string;
}

interface IPendencyUpload {
  formData: {
    laudos: File;
  } | FormData;
  hospitalId: string;
  tipoDocumento: string;
}
